"use client";
import { useEffect, useState } from "react";

interface WebBuilderPreloaderProps {
  message?: string;
  progress?: number;
  showProgress?: boolean;
  isExiting?: boolean;
  onExitComplete?: () => void;
}

export function WebBuilderPreloader({
  message = "Loading Web Builder...",
  progress,
  showProgress = false,
  isExiting = false,
  onExitComplete,
}: WebBuilderPreloaderProps) {
  const [loadingStep, setLoadingStep] = useState(0);
  const [animatedProgress, setAnimatedProgress] = useState(0);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);

  const loadingSteps = [
    "Initializing editor...",
    "Loading components...",
    "Setting up canvas...",
    "Preparing workspace...",
    "Almost ready...",
  ];

  // Handle exit animation
  useEffect(() => {
    if (isExiting && !isAnimatingOut) {
      setIsAnimatingOut(true);
    }
  }, [isExiting, isAnimatingOut]);

  // Cycle through loading steps
  useEffect(() => {
    const interval = setInterval(() => {
      setLoadingStep((prev) => (prev + 1) % loadingSteps.length);
    }, 150000);

    return () => clearInterval(interval);
  }, [loadingSteps.length]);

  // Animate progress bar
  useEffect(() => {
    if (typeof progress === "number") {
      const timer = setTimeout(() => {
        setAnimatedProgress(progress);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [progress]);

  return (
    <>
      {/* Custom styles for exit animation */}
      <style>{`
        .preloader-exit {
          animation: simplefadeOut 0.7s cubic-bezier(0.83, 0, 0.17, 1) forwards;
        }

        .preloader-content-exit {
          animation: contentFadeOut 0.7s cubic-bezier(0.83, 0, 0.17, 1) forwards;
        }

        @keyframes simplefadeOut {
          0% {
            opacity: 1;
          }
          100% {
            opacity: 0;
          }
        }

        @keyframes contentFadeOut {
          0% {
            opacity: 1;
          }
          100% {
            opacity: 0;
          }
        }

        @keyframes textFadeIn {
          0% {
            opacity: 0;
            transform: translateY(5px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      <div
        className={`fixed inset-0 bg-background dark:bg-auth-background z-50 flex items-center justify-center overflow-hidden ${
          isAnimatingOut ? "preloader-exit" : ""
        }`}
        onAnimationEnd={() => {
          if (isAnimatingOut) {
            onExitComplete?.();
          }
        }}
      >
        {/* Background Pattern */}
        <div
          className={`absolute inset-0 opacity-5 transition-opacity duration-700 ${
            isAnimatingOut ? "opacity-0" : "opacity-5"
          }`}
          style={{ transitionTimingFunction: "cubic-bezier(0.83, 0, 0.17, 1)" }}
        >
          <div className="absolute inset-0 bg-grid-pattern"></div>
        </div>

        {/* Animated Background Gradients */}
        <div
          className={`absolute inset-0 transition-opacity duration-700 ${
            isAnimatingOut ? "opacity-0" : "opacity-100"
          }`}
          style={{ transitionTimingFunction: "cubic-bezier(0.83, 0, 0.17, 1)" }}
        >
          <div className="absolute top-20 left-20 w-72 h-72 bg-primary/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-500"></div>
        </div>

        {/* Main Content */}
        <div
          className={`relative z-10 text-center space-y-8 max-w-md mx-auto px-4 ${
            isAnimatingOut ? "preloader-content-exit" : ""
          }`}
        >
          {/* Logo/Icon Area */}
          <div className="relative">
            {/* Spinning Rings */}
            <div className="relative w-40 h-40 mx-auto">
              <div className="absolute inset-0 border-4 border-primary/20 rounded-full"></div>
              <div className="absolute inset-3 border-4 border-primary/40 rounded-full animate-spin"></div>
              <div className="absolute inset-6 border-4 border-primary/60 rounded-full animate-spin-reverse"></div>
              <div className="absolute inset-9 border-4 border-primary rounded-full animate-pulse"></div>

              {/* Center Icon */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-12 h-12 bg-primary rounded-lg animate-pulse shadow-lg flex items-center justify-center">
                  <svg
                    width="352"
                    height="352"
                    viewBox="0 0 352 352"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-8 h-8"
                  >
                    <path
                      d="M21.5559 86.5221C10.5917 42.6651 43.7227 0 89.0098 0H351.198L123.571 130.141C83.766 153.023 32.7585 131.332 21.5559 86.5221Z"
                      fill="white"
                    />
                    <path
                      d="M329.754 264.809C340.719 308.666 307.587 351.331 262.3 351.331H0.112305L227.739 221.19C267.783 198.308 318.552 220.237 329.754 264.809Z"
                      fill="white"
                    />
                    <path
                      d="M175.778 219.523C199.999 219.523 219.635 199.887 219.635 175.666C219.635 151.444 199.999 131.809 175.778 131.809C151.556 131.809 131.921 151.444 131.921 175.666C131.921 199.887 151.556 219.523 175.778 219.523Z"
                      fill="white"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Main Loading Message */}
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-foreground min-h-[2.5rem] animate-fadeInUp">
              {message}
            </h2>

            {/* Dynamic Step Message */}
            <p className="text-muted-foreground min-h-[1.5rem] animate-fadeInUp delay-200">
              {loadingSteps[loadingStep]}
            </p>
          </div>

          {/* Progress Bar (if enabled) */}
          {showProgress && (
            <div className="space-y-3 animate-fadeInUp delay-300 min-w-[300px]">
              <div className="w-full bg-gray-200 dark:bg-muted rounded-full h-2 overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${animatedProgress}%` }}
                >
                  <div className="h-full bg-gradient-shimmer animate-shimmerSlide"></div>
                </div>
              </div>
              {typeof progress === "number" && (
                <p className="text-sm text-muted-foreground min-h-[1.25rem]">
                  {Math.round(animatedProgress)}% Complete
                </p>
              )}
            </div>
          )}

          {/* Floating Elements */}
          <div className="absolute -top-10 -left-10 w-4 h-4 bg-primary/30 rounded-full animate-bounce delay-300"></div>
          <div className="absolute -top-6 -right-8 w-3 h-3 bg-blue-500/30 rounded-full animate-bounce delay-700"></div>
          <div className="absolute -bottom-8 -left-6 w-2 h-2 bg-purple-500/30 rounded-full animate-bounce delay-1000"></div>
          <div className="absolute -bottom-10 -right-10 w-5 h-5 bg-green-500/30 rounded-full animate-bounce delay-500"></div>

          {/* Pulsing Dots */}
          <div className="flex justify-center space-x-2 animate-fadeInUp delay-400">
            <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
            <div className="w-2 h-2 bg-primary rounded-full animate-pulse delay-200"></div>
            <div className="w-2 h-2 bg-primary rounded-full animate-pulse delay-400"></div>
          </div>

          {/* Subtle hint text - positioned to avoid overlap with progress bar */}
          <div
            className={`mt-8 text-xs text-muted-foreground/60 animate-pulse ${showProgress ? "mt-4" : ""}`}
          >
            Powered by Shiphouse
          </div>
        </div>
      </div>
    </>
  );
}
